'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import posthog from 'posthog-js';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Link from 'next/link';
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, Sparkles,
  ShieldAlert, Target, RefreshCw, History, ChevronDown, FileText,
} from 'lucide-react';
import {
  ResearchResponse,
  ResearchStatusResponse,
  ResearchHistoryEntry,
  RESEARCH_CONFIG,
} from '@/types/research';
import { getNvidiaDemoReport } from '@/lib/researchDemo';
import { ResearchLimitModal } from './ResearchLimitModal';
import ResearchLoadingGame from './ResearchLoadingGame';
import { Modal } from './Modal';
import { Button } from './Button';
import { getApiBase } from '@/lib/apiBase';

interface ResearchTabProps {
  symbol: string;
  isPaid: boolean;
}

// Darker, more accessible greens with good contrast against white text
// strong_buy: #15803d (WCAG AA 4.5:1 on white), buy: #16a34a
const RATING_COLORS: Record<string, string> = {
  strong_buy: '#15803d',
  buy: '#16a34a',
  hold: '#a16207',
  sell: '#dc2626',
  strong_sell: '#b91c1c',
};

// Gradient stops for the spectrum bar (left = Strong Buy, right = Strong Sell)
const SPECTRUM_GRADIENT = 'linear-gradient(to right, #15803d, #16a34a, #a16207, #dc2626, #b91c1c)';

// Position of each rating on the spectrum (0–100%)
const RATING_POSITION: Record<string, number> = {
  strong_buy: 10,
  buy: 30,
  hold: 50,
  sell: 70,
  strong_sell: 90,
};

const RATING_LABELS: Record<string, string> = {
  strong_buy: 'Strong Buy',
  buy: 'Buy',
  hold: 'Hold',
  sell: 'Sell',
  strong_sell: 'Strong Sell',
};

const IMPACT_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  positive: { bg: 'rgba(34,197,94,0.1)', text: '#4ade80', border: 'rgba(34,197,94,0.3)' },
  neutral: { bg: 'rgba(156,163,175,0.1)', text: '#9ca3af', border: 'rgba(156,163,175,0.3)' },
  negative: { bg: 'rgba(239,68,68,0.1)', text: '#f87171', border: 'rgba(239,68,68,0.3)' },
};

const IMPACT_DOT_COLORS: Record<string, string> = {
  positive: '#4ade80',
  neutral: '#9ca3af',
  negative: '#f87171',
};

const IMPACT_ORDER: Record<string, number> = {
  positive: 0,
  negative: 1,
  neutral: 2,
};

const TREND_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  bullish: { icon: <TrendingUp size={16} />, color: '#4ade80', label: 'Bullish' },
  neutral: { icon: <Minus size={16} />, color: '#9ca3af', label: 'Neutral' },
  bearish: { icon: <TrendingDown size={16} />, color: '#f87171', label: 'Bearish' },
};

function formatMetricValue(key: string, value: number | null): string {
  if (value === null || value === undefined) return 'N/A';
  switch (key) {
    case 'dividendYield':
    case 'revenueGrowth':
    case 'profitMargin':
      return `${(value * 100).toFixed(2)}%`;
    case 'pe':
    case 'forwardPe':
    case 'priceToBook':
    case 'debtToEquity':
    case 'beta':
      return value.toFixed(2);
    case 'eps':
      return `$${value.toFixed(2)}`;
    case 'fiftyTwoWeekLow':
    case 'fiftyTwoWeekHigh':
      return `$${value.toFixed(2)}`;
    default:
      return value.toFixed(2);
  }
}

function getMetricColor(key: string, value: number | null): string | undefined {
  if (value === null || value === undefined) return undefined;
  switch (key) {
    case 'revenueGrowth':
      return value > 0 ? '#4ade80' : value < 0 ? '#f87171' : undefined;
    case 'profitMargin':
      return value > 0.15 ? '#4ade80' : value < 0 ? '#f87171' : undefined;
    case 'beta':
      return value > 1.5 ? '#f87171' : value < 0.8 ? '#4ade80' : undefined;
    case 'debtToEquity':
      return value > 100 ? '#f87171' : value < 30 ? '#4ade80' : undefined;
    default:
      return undefined;
  }
}

const METRIC_GROUPS: Array<{ label: string; keys: string[] }> = [
  { label: 'Valuation', keys: ['pe', 'forwardPe', 'priceToBook'] },
  { label: 'Profitability', keys: ['eps', 'revenueGrowth', 'profitMargin'] },
  { label: 'Risk', keys: ['beta', 'debtToEquity'] },
  { label: 'Range', keys: ['fiftyTwoWeekLow', 'fiftyTwoWeekHigh', 'dividendYield'] },
];

const METRIC_LABELS: Record<string, string> = {
  pe: 'P/E Ratio',
  forwardPe: 'Forward P/E',
  eps: 'EPS',
  dividendYield: 'Dividend Yield',
  beta: 'Beta',
  fiftyTwoWeekLow: '52-Week Low',
  fiftyTwoWeekHigh: '52-Week High',
  priceToBook: 'Price/Book',
  debtToEquity: 'Debt/Equity',
  revenueGrowth: 'Revenue Growth',
  profitMargin: 'Profit Margin',
};

const METRIC_TOOLTIPS: Record<string, string> = {
  pe: 'Price-to-Earnings ratio. Compares stock price to earnings per share. Lower may indicate undervaluation.',
  forwardPe: 'P/E ratio based on projected future earnings. Useful for comparing expected growth.',
  eps: 'Earnings Per Share. Net profit divided by outstanding shares.',
  dividendYield: 'Annual dividend as a percentage of stock price.',
  beta: 'Measures volatility relative to the market. >1 means more volatile, <1 means less.',
  fiftyTwoWeekLow: 'Lowest trading price in the past 52 weeks.',
  fiftyTwoWeekHigh: 'Highest trading price in the past 52 weeks.',
  priceToBook: 'Stock price relative to book value. Lower may suggest undervaluation.',
  debtToEquity: 'Total debt relative to shareholder equity. Higher means more leveraged.',
  revenueGrowth: 'Year-over-year change in total revenue.',
  profitMargin: 'Percentage of revenue retained as profit after expenses.',
};

export const ResearchTab: React.FC<ResearchTabProps> = ({ symbol, isPaid }) => {
  const [data, setData] = useState<ResearchResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);
  const [expandedTechnical, setExpandedTechnical] = useState<string | null>(null);
  const [limitInfo, setLimitInfo] = useState<{ used: number; limit: number }>({ used: 0, limit: RESEARCH_CONFIG.monthlyLimitPerUser });
  const [history, setHistory] = useState<ResearchHistoryEntry[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyMenuRef = useRef<HTMLDivElement>(null);

  const activeReportDate = data?.report.generatedAt ?? null;

  const loadStatus = useCallback(async () => {
    if (!isPaid) {
      const demoReport = getNvidiaDemoReport();
      setData({
        report: demoReport,
        cachedAt: demoReport.generatedAt,
        isDemo: true,
      });
      posthog.capture('research_report_viewed', { symbol: 'NVDA', is_demo: true });
      setStatusLoading(false);
      return;
    }

    setStatusLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${getApiBase()}/api/research/${encodeURIComponent(symbol)}`,
      );

      if (!res.ok) {
        throw new Error(`Status check failed: ${res.status}`);
      }

      const result: ResearchStatusResponse = await res.json();
      setLimitInfo(result.usage);

      if (result.hasReport && result.report) {
        setData({
          report: result.report,
          cachedAt: result.cachedAt ?? result.report.generatedAt,
          isDemo: false,
        });
        posthog.capture('research_report_viewed', {
          symbol: result.report.symbol,
          is_demo: false,
          source: 'cached',
        });
      } else {
        setData(null);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      posthog.captureException(error, { context: 'research_tab_status', symbol });
      setError('Unable to load research data. Please try again.');
    } finally {
      setStatusLoading(false);
    }
  }, [symbol, isPaid]);

  const loadHistory = useCallback(async () => {
    if (!isPaid) return;
    try {
      const res = await fetch(
        `${getApiBase()}/api/research/${encodeURIComponent(symbol)}/history`,
      );
      if (!res.ok) return;
      const body: { reports: ResearchHistoryEntry[] } = await res.json();
      setHistory(body.reports);
    } catch (err) {
      console.warn('Failed to load research history:', err);
    }
  }, [symbol, isPaid]);

  const generateReport = useCallback(async () => {
    setShowConfirmModal(false);
    setGenerating(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120_000);

    try {
      const res = await fetch(
        `${getApiBase()}/api/research/${encodeURIComponent(symbol)}`,
        { method: 'POST', signal: controller.signal },
      );

      if (res.status === 429) {
        const body = await res.json();
        setLimitInfo({ used: body.used, limit: body.limit });
        setShowLimitModal(true);
        setGenerating(false);
        return;
      }

      if (!res.ok) {
        let serverError: string | undefined;
        try {
          const body = await res.json();
          serverError = body?.error;
        } catch {
          // non-JSON response from upstream; fall through to generic message
        }
        throw new Error(
          `Research API ${res.status}${serverError ? `: ${serverError}` : ''}`,
        );
      }

      const result: ResearchResponse = await res.json();
      setData(result);
      if (result.usage) {
        setLimitInfo(result.usage);
      }

      posthog.capture('research_report_viewed', {
        symbol: result.report.symbol,
        is_demo: false,
        source: 'generated',
      });

      loadHistory();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      const isAbort = error.name === 'AbortError';
      posthog.captureException(error, {
        context: 'research_tab_generate',
        symbol,
        timed_out: isAbort,
      });
      setError(
        isAbort
          ? 'Research report took too long to generate. Please try again.'
          : 'Unable to generate research report. Please try again.',
      );
    } finally {
      clearTimeout(timeoutId);
      setGenerating(false);
    }
  }, [symbol, loadHistory]);

  const loadHistoricalReport = useCallback(async (generatedAt: string) => {
    setHistoryOpen(false);
    if (generatedAt === activeReportDate) return;

    setStatusLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${getApiBase()}/api/research/${encodeURIComponent(symbol)}/history/${encodeURIComponent(generatedAt)}`,
      );
      if (!res.ok) {
        throw new Error(`History API ${res.status}`);
      }
      const result: ResearchResponse = await res.json();
      setData(result);
      posthog.capture('research_report_viewed', {
        symbol: result.report.symbol,
        is_demo: false,
        source: 'history',
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      posthog.captureException(error, { context: 'research_tab_history', symbol });
      setError('Unable to load that report. Please try again.');
    } finally {
      setStatusLoading(false);
    }
  }, [symbol, activeReportDate]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!historyOpen) return;
    function handleClick(e: MouseEvent) {
      if (historyMenuRef.current && !historyMenuRef.current.contains(e.target as Node)) {
        setHistoryOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [historyOpen]);

  const remaining = limitInfo.limit - limitInfo.used;

  // Modals are rendered once at the bottom so they don't disappear when the
  // surrounding view branch swaps (loading → empty → report).
  const modals = (
    <>
      <ResearchLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        used={limitInfo.used}
        limit={limitInfo.limit}
      />
      <GenerateConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={generateReport}
        symbol={symbol}
        remaining={remaining}
      />
    </>
  );

  // Errors take precedence over the loading game — the game has no error
  // state and would otherwise cycle tips forever, hiding the failure.
  if (error) {
    return (
      <>
        <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: 'var(--color-surface)' }}>
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <p className="text-[var(--color-text)] font-medium mb-2">{error}</p>
          <Button
            onClick={() => {
              setError(null);
              loadStatus();
            }}
          >
            <RefreshCw size={14} className="mr-2" /> Retry
          </Button>
        </div>
        {modals}
      </>
    );
  }

  if (generating) {
    return <ResearchLoadingGame ready={false} limitHit={showLimitModal} />;
  }

  if (statusLoading && !data) {
    return (
      <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: 'var(--color-surface)' }}>
        <div className="flex flex-col items-center gap-3 text-[var(--color-text-secondary)]">
          <RefreshCw size={20} className="animate-spin" />
          <span className="text-sm">Loading research…</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <>
        <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: 'var(--color-surface)' }}>
          <Sparkles className="w-10 h-10 mx-auto mb-4" style={{ color: 'var(--color-accent-muted)' }} />
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-2">
            No research report yet for {symbol.toUpperCase()}
          </h3>
          <p className="text-sm text-[var(--color-text-secondary)] mb-4 max-w-md mx-auto">
            Generate a fresh AI-powered research report with analyst rating,
            macro factors, technical analysis and more.
          </p>
          <p className="text-xs text-[var(--color-text-muted)] mb-6">
            This will use 1 of your {remaining} remaining reports this month.
          </p>
          <Button onClick={() => setShowConfirmModal(true)} disabled={remaining <= 0}>
            <Sparkles size={14} className="mr-2" /> Generate Report
          </Button>
        </div>
        {modals}
      </>
    );
  }

  const { report, isDemo } = data;
  const rating = report.analystRating;
  const ratingColor = RATING_COLORS[rating.consensus] || '#9ca3af';
  const trend = TREND_CONFIG[report.technicalAnalysis.trend] || TREND_CONFIG.neutral;

  // Sort macro factors: positive first, then negative, then neutral
  const sortedMacroFactors = [...report.macroFactors].sort(
    (a, b) => (IMPACT_ORDER[a.impact] ?? 2) - (IMPACT_ORDER[b.impact] ?? 2)
  );

  const tickerPosition = RATING_POSITION[rating.consensus] ?? 50;

  return (
    <div className="space-y-6">
      {/* Demo Banner */}
      {isDemo && (
        <div className="rounded-2xl p-4 border" style={{ backgroundColor: 'rgba(245,158,11,0.1)', borderColor: 'rgba(245,158,11,0.3)' }}>
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium text-sm">Demo Report — NVIDIA</p>
              <p className="text-amber-400/70 text-sm mt-1">
                This is a sample research report.{' '}
                <Link href="/checkout" className="underline font-medium text-amber-400">
                  Upgrade to Pro
                </Link>{' '}
                to generate reports on any stock in your portfolio.
              </p>
            </div>
          </div>
        </div>
      )}

      {!isDemo && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
            <Sparkles size={12} />
            <span>
              {remaining} of {limitInfo.limit} reports remaining this month
            </span>
          </div>
          <div className="flex items-center gap-2">
            {history.length > 0 && (
              <div className="relative" ref={historyMenuRef}>
                <button
                  type="button"
                  onClick={() => setHistoryOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-primary)' }}
                >
                  <History size={14} />
                  Past reports ({history.length})
                  <ChevronDown size={14} />
                </button>
                {historyOpen && (
                  <ul
                    className="absolute right-0 mt-2 w-72 rounded-xl shadow-2xl z-20 max-h-96 overflow-y-auto py-1"
                    style={{ backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-border-subtle)' }}
                  >
                    {history.map((h) => {
                      const date = new Date(h.generatedAt);
                      const isActive = h.generatedAt === activeReportDate;
                      return (
                        <li key={h.generatedAt}>
                          <button
                            type="button"
                            onClick={() => loadHistoricalReport(h.generatedAt)}
                            className="w-full flex items-start justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--color-surface-secondary)]"
                            style={{ color: 'var(--color-text)' }}
                          >
                            <div className="flex items-start gap-2 min-w-0">
                              <FileText size={14} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                              <div className="min-w-0">
                                <div className="font-medium">
                                  {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </div>
                                <div className="text-[11px] text-[var(--color-text-muted)]">
                                  {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                  {' · '}
                                  {RATING_LABELS[h.consensus]} · {h.confidence}%
                                </div>
                              </div>
                            </div>
                            {isActive && (
                              <span className="text-[10px] uppercase tracking-wider mt-1" style={{ color: 'var(--color-accent-muted)' }}>
                                current
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            <Button size="sm" onClick={() => setShowConfirmModal(true)}>
              <RefreshCw size={14} className="mr-2" />
              Generate new report
            </Button>
          </div>
        </div>
      )}

      {/* 1A: Analyst Rating + 1B: Macro Factors */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Analyst Rating */}
        <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Analyst Rating</h3>
          <div className="flex items-center gap-4 mb-5">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
              style={{ backgroundColor: ratingColor }}
            >
              {RATING_LABELS[rating.consensus] || rating.consensus}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl font-bold text-[var(--color-text)]">{rating.confidence}%</span>
                <span className="text-sm text-[var(--color-text-secondary)]">confidence</span>
              </div>
              <p className="text-sm text-[var(--color-text-secondary)]">{rating.reasoning}</p>
            </div>
          </div>

          {/* Spectrum bar with ticker */}
          <div className="relative pt-10 pb-1">
            {/* Ticker label above the bar */}
            <div
              className="absolute top-0 flex flex-col items-center"
              style={{ left: `${tickerPosition}%`, transform: 'translateX(-50%)' }}
            >
              <span className="text-[10px] font-semibold text-[var(--color-text)] whitespace-nowrap">
                {RATING_LABELS[rating.consensus]}
              </span>
              <span
                className="w-0 h-0 mt-1"
                style={{
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderTop: `6px solid ${ratingColor}`,
                }}
              />
              <span className="w-px h-2" style={{ backgroundColor: ratingColor }} />
            </div>
            {/* Gradient bar */}
            <div
              className="h-2.5 rounded-full w-full"
              style={{ background: SPECTRUM_GRADIENT }}
            />
            {/* Scale labels */}
            <div className="flex justify-between mt-1.5">
              <span className="text-[10px] text-[var(--color-text-muted)]">Strong Buy</span>
              <span className="text-[10px] text-[var(--color-text-muted)]">Strong Sell</span>
            </div>
          </div>
        </div>

        {/* Macro Factors — sorted: positive, negative, neutral */}
        {report.macroFactors.length > 0 && (
          <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Macro Factors</h3>
            <div className="space-y-3">
              {sortedMacroFactors.map((factor, i) => {
                const colors = IMPACT_COLORS[factor.impact] || IMPACT_COLORS.neutral;
                const dotColor = IMPACT_DOT_COLORS[factor.impact] || '#9ca3af';
                return (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: dotColor }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm text-[var(--color-text)]">{factor.factor}</span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-medium capitalize"
                          style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
                        >
                          {factor.impact}
                        </span>
                      </div>
                      <p className="text-sm text-[var(--color-text-secondary)]">{factor.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 2A: Technical Analysis + 2B: Earnings */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Technical Analysis */}
        <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Technical Analysis</h3>
          <div className="grid gap-3 grid-cols-3 mb-4">
            <button type="button" className="rounded-xl p-3 text-left" style={{ backgroundColor: 'var(--color-surface-secondary)' }} onClick={() => setExpandedTechnical(expandedTechnical === 'support' ? null : 'support')}>
              <span className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider underline decoration-dotted underline-offset-2 cursor-help">
                Support
              </span>
              <p className="text-base font-semibold text-[var(--color-text)] mt-1 tabular-nums">
                {report.technicalAnalysis.support ? `$${report.technicalAnalysis.support.toFixed(2)}` : 'N/A'}
              </p>
              {expandedTechnical === 'support' && (
                <p className="text-[11px] text-[var(--color-text-muted)] mt-1.5 leading-relaxed">A price floor where buying pressure historically prevents further decline.</p>
              )}
            </button>
            <button type="button" className="rounded-xl p-3 text-left" style={{ backgroundColor: 'var(--color-surface-secondary)' }} onClick={() => setExpandedTechnical(expandedTechnical === 'resistance' ? null : 'resistance')}>
              <span className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider underline decoration-dotted underline-offset-2 cursor-help">
                Resistance
              </span>
              <p className="text-base font-semibold text-[var(--color-text)] mt-1 tabular-nums">
                {report.technicalAnalysis.resistance ? `$${report.technicalAnalysis.resistance.toFixed(2)}` : 'N/A'}
              </p>
              {expandedTechnical === 'resistance' && (
                <p className="text-[11px] text-[var(--color-text-muted)] mt-1.5 leading-relaxed">A price ceiling where selling pressure historically prevents further rise.</p>
              )}
            </button>
            <button type="button" className="rounded-xl p-3 text-left" style={{ backgroundColor: 'var(--color-surface-secondary)' }} onClick={() => setExpandedTechnical(expandedTechnical === 'trend' ? null : 'trend')}>
              <span className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider underline decoration-dotted underline-offset-2 cursor-help">Trend</span>
              <div className="flex items-center gap-2 mt-1">
                <span style={{ color: trend.color }}>{trend.icon}</span>
                <span className="text-base font-semibold" style={{ color: trend.color }}>{trend.label}</span>
              </div>
              {expandedTechnical === 'trend' && (
                <p className="text-[11px] text-[var(--color-text-muted)] mt-1.5 leading-relaxed">The overall price direction — bullish (upward), bearish (downward), or neutral (sideways).</p>
              )}
            </button>
          </div>
          {report.technicalAnalysis.signals.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider">Signals</span>
              <ul className="space-y-1">
                {report.technicalAnalysis.signals.map((signal, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
                    <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: 'var(--color-text-muted)' }} />
                    {signal}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Earnings Info */}
        {report.earningsInfo.nextDate && (
          <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Earnings</h3>
            <p className="text-sm text-[var(--color-text-secondary)] mb-3">
              <span className="text-[var(--color-text-muted)]">Next earnings date:</span>{' '}
              <span className="text-[var(--color-text)] font-medium">
                {new Date(report.earningsInfo.nextDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </p>
            {report.earningsInfo.recentHighlights.length > 0 && (
              <ul className="space-y-1.5">
                {report.earningsInfo.recentHighlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
                    <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: 'var(--color-text-muted)' }} />
                    {h}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Key Metrics — grouped with 2-column grid, tooltips, and tighter layout */}
      <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Key Metrics</h3>
        <div className="grid gap-6 sm:grid-cols-2">
          {METRIC_GROUPS.map((group) => (
            <div key={group.label}>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                {group.label}
              </span>
              <div className="mt-1.5 space-y-0.5">
                {group.keys.map((key) => {
                  const value = report.keyMetrics[key as keyof typeof report.keyMetrics];
                  const color = getMetricColor(key, value);
                  const tooltip = METRIC_TOOLTIPS[key];
                  const isExpanded = expandedMetric === key;
                  return (
                    <div key={key}>
                      <div className="flex items-baseline gap-2">
                        <button
                          type="button"
                          onClick={() => tooltip && setExpandedMetric(isExpanded ? null : key)}
                          className={`text-sm text-left ${tooltip ? 'underline decoration-dotted decoration-[var(--color-text-muted)] underline-offset-2 cursor-help' : ''} text-[var(--color-text-secondary)]`}
                        >
                          {METRIC_LABELS[key] || key}
                        </button>
                        <span className="flex-1 border-b border-dotted" style={{ borderColor: 'var(--color-surface-secondary)' }} />
                        <span
                          className="text-sm font-medium tabular-nums flex-shrink-0"
                          style={{ color: color || 'var(--color-text)' }}
                        >
                          {formatMetricValue(key, value)}
                        </span>
                      </div>
                      {isExpanded && tooltip && (
                        <p className="text-xs text-[var(--color-text-muted)] mt-0.5 mb-1 leading-relaxed">
                          {tooltip}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Factors & Opportunities */}
      <div className="grid gap-6 lg:grid-cols-2">
        {report.riskFactors.length > 0 && (
          <div className="rounded-2xl p-6 border" style={{ backgroundColor: 'rgba(239,68,68,0.05)', borderColor: 'rgba(239,68,68,0.2)' }}>
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <ShieldAlert size={18} className="text-red-400" />
              Risk Factors
            </h3>
            <ul className="space-y-2">
              {report.riskFactors.map((risk, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                  {risk}
                </li>
              ))}
            </ul>
          </div>
        )}

        {report.opportunities.length > 0 && (
          <div className="rounded-2xl p-6 border" style={{ backgroundColor: 'rgba(34,197,94,0.05)', borderColor: 'rgba(34,197,94,0.2)' }}>
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <Target size={18} className="text-green-400" />
              Opportunities
            </h3>
            <ul className="space-y-2">
              {report.opportunities.map((opp, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />
                  {opp}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Bottom Summary (formerly Executive Summary) */}
      <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
          <Sparkles size={18} style={{ color: 'var(--color-accent-muted)' }} />
          Narrative
        </h3>
        <div className="prose prose-invert prose-sm max-w-none text-[var(--color-text-secondary)] [&_strong]:text-[var(--color-text-primary)] [&_a]:text-[var(--color-accent-muted)]">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {report.summary}
          </ReactMarkdown>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="rounded-2xl p-4 border text-center" style={{ backgroundColor: 'rgba(245,158,11,0.05)', borderColor: 'rgba(245,158,11,0.2)' }}>
        <div className="flex items-start justify-center gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-400/70">{report.disclaimer}</p>
        </div>
      </div>

      <p className="text-center text-xs text-[var(--color-text-muted)]">
        Report generated on {new Date(report.generatedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </p>

      {modals}
    </div>
  );
};

interface GenerateConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  symbol: string;
  remaining: number;
}

function GenerateConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  symbol,
  remaining,
}: GenerateConfirmModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Generate report for ${symbol.toUpperCase()}`}>
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Generating a new research report runs a fresh AI analysis and will use
          1 of your {remaining} remaining reports this month.
        </p>
        <p className="text-xs text-[var(--color-text-muted)]">
          Reports take about 30–60 seconds to generate and are saved to your
          history. Viewing a past report is always free.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm} disabled={remaining <= 0}>
            <Sparkles size={14} className="mr-2" />
            Spend credit & generate
          </Button>
        </div>
      </div>
    </Modal>
  );
}
